/**
 * ONB-0087: Kestrel Drives A/S, the second source for 50 HP, 480 V motor
 * drives. The richest case in the demo: every vendor-master item the
 * onboarding agent collects, with the document each value was extracted from.
 */
import { getPerson } from '../../data/company';
import { daysAgo, daysFromNow, fmtDate } from '../../data/dates';
import { getSupplier } from '../../data/suppliers';
import type { PersonId } from '../../data/types';
import type {
  ExtractedField,
  OnboardingCase,
  SourceRef,
  StepContent,
} from './onboarding-types';

export const KESTREL_CASE_ID = 'ONB-0087';
export const KESTREL_SUPPLIER_MASTER_ID = '100482';
export const KESTREL_RISK_TIER =
  'Level 2: critical material, low supplier risk';
/** The Procurement approver who creates the supplier in the ERP. */
export const PROCUREMENT_APPROVER: PersonId = 'sarah-chen';

const DOC = {
  cvr: 'Kestrel_Company_Registration_Extract.pdf',
  w8: 'Kestrel_Foreign_Tax_Form.pdf',
  bank: 'Bank_confirmation_Danske_Bank.pdf',
  annualReport: 'Kestrel_Annual_Report_2025.pdf',
  attestations: 'Kestrel_Compliance_Attestations_signed.pdf',
  ul: 'UL_Listing_Certificate_E478215.pdf',
  ce: 'EU_Safety_Declarations.pdf',
  iso9001: 'ISO9001_certificate_2024.pdf',
  questionnaire: 'Kestrel_Supplier_Quality_Questionnaire.xlsx',
  coi: 'COI_2026.pdf',
} as const;

function fromDoc(name: string, confidence: number): SourceRef {
  return { name, kind: 'document', confidence };
}

function fromLookup(name: string, confidence = 100): SourceRef {
  return { name, kind: 'lookup', confidence };
}

const PORTAL: SourceRef = {
  name: 'Supplier portal form',
  kind: 'portal',
  confidence: 100,
};

function field(
  label: string,
  value: string,
  source: SourceRef,
  extra?: Pick<ExtractedField, 'mono' | 'tone'>,
): ExtractedField {
  return { label, value, source, ...extra };
}

const supplier = getSupplier('sup-kestrel');
const priya = getPerson('priya-raman').name;

const PROFILE: StepContent = {
  summary:
    'Legal entity, ownership and contacts extracted from the company registry extract and cross-checked against two registries.',
  fieldGroups: [
    {
      title: 'Legal entity',
      fields: [
        field('Legal name', supplier.name, fromDoc(DOC.cvr, 99)),
        field(
          'Company registration and VAT number',
          'DK-38194427',
          fromDoc(DOC.cvr, 99),
          {
            mono: true,
          },
        ),
        field(
          'Global company number (D-U-N-S)',
          supplier.duns,
          fromLookup('Dun & Bradstreet'),
          {
            mono: true,
          },
        ),
        field(
          'Legal form',
          'Aktieselskab (A/S), public limited company',
          fromDoc(DOC.cvr, 98),
        ),
        field(
          'Registered address',
          'Fabriksvej 21, 6430 Nordborg, Denmark',
          fromDoc(DOC.cvr, 97),
        ),
        field(
          'Manufacturing site',
          'Nordborg, Denmark (drive assembly, power-stage build and final test)',
          fromDoc(DOC.cvr, 94),
        ),
        field(
          'Founded, employees',
          '2011, about 310 employees',
          fromDoc(DOC.cvr, 96),
        ),
        field('Website', 'www.kestreldrives.dk', fromLookup('Company website')),
      ],
    },
    {
      title: 'Ownership',
      fields: [
        field(
          'Parent company',
          'Nordlys Industrial Holding ApS, Copenhagen',
          fromDoc(DOC.cvr, 97),
        ),
        field(
          'Ownership',
          'Nordlys Industrial Holding 78%, founders and management 22%',
          fromDoc(DOC.cvr, 95),
        ),
        field(
          'Ultimate owner',
          'Anders Holm, 41% indirect. Above the 25% threshold, screened in Legal and compliance.',
          fromDoc(DOC.cvr, 93),
        ),
      ],
    },
    {
      title: 'Primary contacts',
      fields: [
        field(
          'Commercial',
          `${supplier.primaryContact.name}, ${supplier.primaryContact.title}`,
          PORTAL,
        ),
        field(
          'Executive sponsor',
          'Søren Lindgren, Chief Executive Officer',
          PORTAL,
        ),
        field(
          'Quality and regulatory',
          'Ingrid Poulsen, Director Quality and Regulatory',
          PORTAL,
        ),
      ],
    },
  ],
  checkGroups: [
    {
      checks: [
        {
          label: 'Registry match',
          outcome: 'pass',
          checkedDaysAgo: 8,
          detail:
            'Active and in good standing since 2011. Name and address match the extract.',
          source: 'Danish Business Authority',
        },
        {
          label: 'Business ID match',
          outcome: 'pass',
          checkedDaysAgo: 8,
          detail:
            'Legal name, address and parent company match the Dun & Bradstreet record.',
          source: 'Dun & Bradstreet',
        },
        {
          label: 'Duplicate supplier check',
          outcome: 'pass',
          checkedDaysAgo: 8,
          detail:
            'No supplier in the ERP shares this tax ID, bank account or address.',
          source: 'Supplier records',
        },
      ],
    },
  ],
  documents: [
    { name: DOC.cvr, state: 'received', daysAgo: 8, fieldsExtracted: 11 },
  ],
};

const TAX_BANKING: StepContent = {
  summary:
    'Foreign tax form on file and bank account ownership verified by a bank confirmation letter and a callback.',
  fieldGroups: [
    {
      title: 'Tax',
      fields: [
        field(
          'Tax form',
          'US form for non-US companies received, claiming foreign status',
          fromDoc(DOC.w8, 98),
        ),
        field('Country of residence for tax', 'Denmark', fromDoc(DOC.w8, 99)),
        field('Foreign tax number', 'DK-38194427', fromDoc(DOC.w8, 97), {
          mono: true,
        }),
        field(
          'US withholding tax status',
          'Corporation, operating (non-financial) foreign company',
          fromDoc(DOC.w8, 95),
        ),
        field(
          'Signed by',
          `Søren Lindgren, CEO, on ${fmtDate(daysAgo(6))}`,
          fromDoc(DOC.w8, 96),
        ),
      ],
    },
    {
      title: 'Bank account',
      fields: [
        field('Bank', 'Danske Bank A/S, Copenhagen', fromDoc(DOC.bank, 98)),
        field('Account holder', supplier.name, fromDoc(DOC.bank, 99)),
        field(
          'Bank account (IBAN)',
          'DK•• •••• •••• 4471',
          fromDoc(DOC.bank, 99),
          {
            mono: true,
          },
        ),
        field('Bank code (SWIFT)', 'DABADKKK', fromDoc(DOC.bank, 99), {
          mono: true,
        }),
        field('Payment currency', 'USD', fromDoc(DOC.bank, 96)),
        field(
          'Ownership verification',
          'Bank account ownership verified',
          fromLookup('Onboarding agent'),
          { tone: 'ok' },
        ),
      ],
    },
  ],
  checkGroups: [
    {
      title: 'Bank verification',
      checks: [
        {
          label: 'Bank account format valid',
          outcome: 'pass',
          checkedDaysAgo: 5,
          detail: 'Country DK, 18 characters, check digits correct.',
          source: 'Account number format validation',
        },
        {
          label: 'Account name matches legal name',
          outcome: 'pass',
          checkedDaysAgo: 5,
          detail: `${supplier.name} on the bank confirmation letter dated ${fmtDate(daysAgo(5))}.`,
          source: DOC.bank,
        },
        {
          label: 'No recent bank-detail changes',
          outcome: 'pass',
          checkedDaysAgo: 5,
          detail:
            'First bank record for this supplier and no change requests in the last 90 days.',
          source: 'Supplier records',
        },
        {
          label: 'Callback verification',
          outcome: 'pass',
          checkedDaysAgo: 3,
          detail: `On ${fmtDate(daysAgo(3))} at 11:20 CET the agent called Lise Kjær, Finance Controller, on the number in the company registry (not the one in the email). She confirmed the last four digits (4471) and the account holder. Call note recorded.`,
          source: 'Recorded phone call note',
        },
      ],
    },
  ],
  documents: [
    { name: DOC.w8, state: 'received', daysAgo: 5, fieldsExtracted: 9 },
    { name: DOC.bank, state: 'received', daysAgo: 4, fieldsExtracted: 6 },
  ],
};

const FINANCIAL: StepContent = {
  summary:
    'Low financial risk: credit score 81 of 100, growing revenue and a payment record that is on time.',
  fieldGroups: [
    {
      title: 'Credit and payment behavior',
      fields: [
        field(
          'Credit score',
          '81 / 100, low risk',
          fromLookup('Credit bureau report'),
          {
            tone: 'ok',
          },
        ),
        field(
          'Payment record',
          'Pays on time (index 82)',
          fromLookup('Credit bureau report'),
        ),
        field(
          'Dependency on Pillar',
          'Projected Pillar spend of $6.2M a year is about 4% of Kestrel revenue',
          fromDoc(DOC.annualReport, 91),
        ),
      ],
    },
    {
      title: 'Financials, FY2025',
      fields: [
        field(
          'Revenue',
          '€142M, up 12.7% on FY2024 (€126M)',
          fromDoc(DOC.annualReport, 97),
        ),
        field(
          'Profitability',
          'Operating margin 16.4% (EBITDA €23.3M), net profit €11.4M (8.0%)',
          fromDoc(DOC.annualReport, 96),
        ),
        field(
          'Balance sheet',
          'Equity is 46% of assets, short-term assets cover short-term debts 1.8 times, net debt €33M (1.4 times EBITDA)',
          fromDoc(DOC.annualReport, 94),
        ),
        field(
          'Auditor opinion',
          'Clean audit opinion, no doubt about the company continuing',
          fromDoc(DOC.annualReport, 98),
        ),
      ],
    },
  ],
  checkGroups: [
    {
      checks: [
        {
          label: 'Credit score above the 60 minimum for critical materials',
          outcome: 'pass',
          checkedDaysAgo: 7,
          detail: 'Score 81, up from 76 in FY2023.',
          source: 'Credit bureau report',
        },
        {
          label: 'Supplier dependency on Pillar below 10%',
          outcome: 'pass',
          checkedDaysAgo: 7,
          detail:
            'Projected spend is about 4% of revenue, so Pillar is not too large a share of Kestrel’s business.',
          source: DOC.annualReport,
        },
      ],
    },
  ],
  documents: [
    {
      name: DOC.annualReport,
      state: 'received',
      daysAgo: 7,
      fieldsExtracted: 14,
    },
  ],
  trend: [
    { year: 'FY2023', revenueEurM: 109, creditScore: 76 },
    { year: 'FY2024', revenueEurM: 126, creditScore: 79 },
    { year: 'FY2025', revenueEurM: 142, creditScore: 81 },
  ],
};

const SANCTIONS_SOURCE =
  'Screened: company, 3 directors and 2 beneficial owners';

const LEGAL: StepContent = {
  summary:
    'Seven screenings run by the agent. Six are clear and one negative-news hit was reviewed as low relevance to supply.',
  fieldGroups: [
    {
      title: 'Screened parties',
      fields: [
        field(
          'Entities and people',
          `${supplier.name}, 3 directors, 2 beneficial owners`,
          fromLookup('Onboarding agent'),
        ),
      ],
    },
  ],
  checkGroups: [
    {
      title: 'Screening',
      checks: [
        {
          label: 'US sanctions list',
          outcome: 'pass',
          checkedDaysAgo: 4,
          detail: `Clear, 0 matches. ${SANCTIONS_SOURCE}.`,
          source: 'US Treasury',
        },
        {
          label: 'EU sanctions list',
          outcome: 'pass',
          checkedDaysAgo: 4,
          detail: 'Clear, 0 matches.',
          source: 'EU Financial Sanctions Database',
        },
        {
          label: 'UK sanctions list',
          outcome: 'pass',
          checkedDaysAgo: 4,
          detail: 'Clear, 0 matches.',
          source: 'UK HM Treasury OFSI',
        },
        {
          label: 'UN sanctions list',
          outcome: 'pass',
          checkedDaysAgo: 4,
          detail: 'Clear, 0 matches.',
          source: 'UN consolidated list',
        },
        {
          label: 'Government officials and associates',
          outcome: 'pass',
          checkedDaysAgo: 4,
          detail:
            'Clear. None of the 5 individuals is a government official or a close associate of one.',
          source: 'Government officials database',
        },
        {
          label: 'Negative news',
          outcome: 'flag',
          checkedDaysAgo: 4,
          detail:
            '1 hit in 14 sources: a 2023 patent dispute with a competitor over a motor-control design, settled in 2024 with a cross-license. AI summary: low relevance to supply. No injunction, no product-safety finding, no effect on drive output.',
          source: 'News and legal-notice sweep',
        },
        {
          label: 'Lawsuit search',
          outcome: 'pass',
          checkedDaysAgo: 4,
          detail: 'No open matters in Danish, EU or US court records.',
          source: 'Court records search',
        },
      ],
    },
    {
      title: 'Attestations',
      checks: [
        {
          label: 'Anti-bribery questionnaire',
          outcome: 'pass',
          checkedDaysAgo: 6,
          detail:
            'Signed by the CEO. No government-official interactions in the supply chain for Pillar.',
          source: DOC.attestations,
        },
        {
          label: 'Forced-labor attestation',
          outcome: 'pass',
          checkedDaysAgo: 6,
          detail:
            'Signed. Three of its suppliers (power modules, aluminium heat sinks and copper busbar) declared, all in the EU and US, and screened against the forced-labor import ban list with no match.',
          source: DOC.attestations,
        },
      ],
    },
  ],
  documents: [
    {
      name: DOC.attestations,
      state: 'received',
      daysAgo: 6,
      fieldsExtracted: 3,
    },
  ],
};

const QUALITY: StepContent = {
  summary:
    'UL, EU safety and ISO certificates are in order, and the Nordborg factory is the one UL has certified. The on-site audit is scheduled and one of three sample units is still in test.',
  fieldGroups: [
    {
      title: 'Safety and quality certifications',
      fields: [
        field(
          'UL certification',
          'UL certificate E478215 for motor drives, 50 HP, 480 V series. UL follow-up inspections are active.',
          fromDoc(DOC.ul, 97),
          { tone: 'ok' },
        ),
        field(
          'EU safety marking (CE)',
          'Declarations that the drives meet EU electrical safety and electromagnetic interference rules',
          fromDoc(DOC.ce, 96),
          { tone: 'ok' },
        ),
        field(
          'ISO 9001:2015',
          'Valid until May 14, 2027',
          fromDoc(DOC.iso9001, 97),
          { tone: 'ok' },
        ),
        field(
          'ISO 14001:2015',
          'Valid until Aug 22, 2027',
          fromLookup('Certification body registry'),
          { tone: 'ok' },
        ),
        field(
          'Hazardous substances (RoHS and REACH)',
          'Declarations received. No restricted hazardous substance above 0.1% in the 50 HP drive series.',
          fromDoc(DOC.ce, 94),
          { tone: 'ok' },
        ),
      ],
    },
    {
      title: 'Supplier quality questionnaire',
      fields: [
        field('Overall score', '92 / 100', fromDoc(DOC.questionnaire, 94), {
          tone: 'ok',
        }),
        field(
          'Section scores',
          'Quality system 95, change management 90, complaints and corrective action 91, testing and traceability 93',
          fromDoc(DOC.questionnaire, 93),
        ),
      ],
    },
  ],
  checkGroups: [
    {
      checks: [
        {
          label: 'Certificates in date',
          outcome: 'pass',
          checkedDaysAgo: 7,
          detail:
            'UL, EU safety and ISO 9001 certificates are all valid for more than 12 months.',
          source: `${DOC.ul}, ${DOC.ce}, ${DOC.iso9001}`,
        },
        {
          label: 'Factory is certified by UL',
          outcome: 'pass',
          checkedDaysAgo: 7,
          detail:
            'The factory address on the questionnaire matches the factory named in UL certificate E478215. The agent checks this for every new direct supplier after RSK-301.',
          source: 'UL certification database',
        },
        {
          label: 'Questionnaire above the 80 minimum',
          outcome: 'pass',
          checkedDaysAgo: 6,
          detail: 'Score 92. The lowest section is change control (90).',
          source: DOC.questionnaire,
        },
        {
          label: 'On-site audit',
          outcome: 'pending',
          detail: `Scheduled for ${fmtDate(daysFromNow(18))}, led by ${priya}.`,
          source: 'Onboarding agent',
        },
        {
          label: 'Sample units',
          outcome: 'pending',
          detail: '2 of 3 sample drives passed. Unit KD-VFD-26-019 is in test.',
          source: 'Dallas panel shop test bench',
        },
      ],
    },
  ],
  documents: [
    { name: DOC.ul, state: 'received', daysAgo: 7, fieldsExtracted: 5 },
    { name: DOC.ce, state: 'received', daysAgo: 7, fieldsExtracted: 3 },
    { name: DOC.iso9001, state: 'received', daysAgo: 7, fieldsExtracted: 4 },
    {
      name: DOC.questionnaire,
      state: 'received',
      daysAgo: 6,
      fieldsExtracted: 4,
    },
  ],
  units: [
    {
      serial: 'KD-VFD-26-011',
      result: 'passed',
      detail:
        'Full-load heat test, insulation strength, electrical noise and waveform distortion within specification.',
    },
    {
      serial: 'KD-VFD-26-014',
      result: 'passed',
      detail:
        'Full-load temperature rise and overload tests within specification. Output current within 2% unit to unit.',
    },
    {
      serial: 'KD-VFD-26-019',
      result: 'in_test',
      detail:
        'Heat cycling and 72-hour run-in test pending. Results due in 5 days.',
    },
  ],
  audit: {
    inDays: 18,
    auditor: 'priya-raman',
    scope:
      'ISO 9001 quality system, drive assembly and final test, UL follow-up inspection procedures, change management',
  },
};

const INSURANCE_ESG: StepContent = {
  summary:
    'Product liability cover meets the Pillar minimum and the sustainability profile is strong: EcoVadis Gold, climate score B and ISO 14001.',
  fieldGroups: [
    {
      title: 'Insurance',
      fields: [
        field(
          'Product liability',
          '€10,000,000 per occurrence. Certificate valid until Jan 31, 2027.',
          fromDoc(DOC.coi, 97),
          { tone: 'ok' },
        ),
        field(
          'General liability',
          '€5,000,000 per occurrence',
          fromDoc(DOC.coi, 96),
        ),
        field('Product recall', '€5,000,000', fromDoc(DOC.coi, 95)),
        field('Insurer', 'Nordic Mutual Insurance A/S', fromDoc(DOC.coi, 98)),
        field(
          'Certificate holder',
          'Pillar Industrial Supply, Inc.',
          fromDoc(DOC.coi, 99),
        ),
      ],
    },
    {
      title: 'Sustainability',
      fields: [
        field(
          'Sustainability rating (EcoVadis)',
          'Gold, 72 / 100',
          fromLookup('EcoVadis scorecard'),
          {
            tone: 'ok',
          },
        ),
        field('Climate disclosure score (CDP)', 'B', fromLookup('CDP')),
        field(
          'Environmental management',
          'ISO 14001 certified',
          fromLookup('Certification body registry'),
        ),
        field('Supplier code of conduct', 'Acknowledged and signed', PORTAL),
      ],
    },
  ],
  checkGroups: [
    {
      checks: [
        {
          label: 'Product liability meets the Pillar minimum',
          outcome: 'pass',
          checkedDaysAgo: 4,
          detail:
            '€10M is above the $10M per occurrence Pillar requires for direct suppliers.',
          source: DOC.coi,
        },
        {
          label: 'Sustainability rating at Silver or above',
          outcome: 'pass',
          checkedDaysAgo: 4,
          detail: 'EcoVadis Gold, 72 of 100.',
          source: 'EcoVadis scorecard',
        },
      ],
    },
  ],
  documents: [
    { name: DOC.coi, state: 'received', daysAgo: 4, fieldsExtracted: 5 },
  ],
};

const AGREEMENTS: StepContent = {
  summary:
    'NDA signed. The quality agreement draft has two supplier deviations to resolve, and the supply agreement follows the RFQ-2026-022 award.',
  fieldGroups: [],
  checkGroups: [],
  documents: [],
  agreements: [
    {
      name: 'Mutual NDA',
      detail: 'Covers approval testing of 50 HP, 480 V motor drives.',
      status: 'signed',
      contractId: 'NDA-KEST-2026-001',
    },
    {
      name: 'Quality agreement, draft v1',
      detail:
        'Generated from the Pillar quality agreement template. The agent compared Kestrel redlines with the Pillar standard and flagged 2 deviations.',
      status: 'draft',
    },
    {
      name: 'Supply agreement',
      detail:
        "Generated from the supply agreement template at award, using Kestrel's bid of $6,050 each with duties paid. Pending the RFQ-2026-022 award.",
      status: 'pending',
    },
  ],
  deviations: [
    {
      id: 'change-notice',
      topic: 'Change notification',
      supplierAsk:
        '90 days prior notice of factory, process or component-source changes',
      standard:
        '180 days prior written notice, and Pillar approval before any change of manufacturing site or subcontractor is implemented',
      recommendation: 'Counter with 180 days',
      standardClauseId: 'QA-VITX-2023-004:7.2',
      state: 'open',
    },
    {
      id: 'scar-response',
      topic: 'Corrective action response',
      supplierAsk:
        'Respond to a corrective action request within 20 business days',
      standard: 'Respond within 10 business days',
      recommendation: 'Counter with 10 business days',
      standardClauseId: 'QA-VITX-2023-004:9.1',
      state: 'open',
    },
  ],
};

const APPROVALS: StepContent = {
  summary:
    'Quality, Finance and Legal have approved. Procurement approval is the last step and creates the supplier in the ERP.',
  fieldGroups: [],
  checkGroups: [],
  documents: [],
  approvals: [
    {
      role: 'Quality',
      person: 'priya-raman',
      status: 'approved',
      decidedDaysAgo: 2,
      note: 'UL, EU safety and ISO 9001 certificates verified. Audit and sample unit tests tracked after onboarding.',
    },
    {
      role: 'Finance, bank details',
      person: 'tom-gallagher',
      status: 'approved',
      decidedDaysAgo: 2,
      note: 'Bank account ownership verified by confirmation letter and callback.',
    },
    {
      role: 'Legal',
      person: 'elena-novak',
      status: 'approved',
      decidedDaysAgo: 1,
      note: 'NDA signed. Negative-news hit reviewed, low relevance to supply.',
    },
    {
      role: 'Procurement',
      person: PROCUREMENT_APPROVER,
      status: 'pending',
      note: 'Final approval creates the supplier record and enables the portal.',
    },
  ],
};

const MINUTES_PER_DAY = 24 * 60;

export function buildKestrelCase(): OnboardingCase {
  return {
    id: KESTREL_CASE_ID,
    supplierName: supplier.name,
    supplierId: 'sup-kestrel',
    type: 'direct',
    categoryId: 'cat-drives',
    purpose: 'Second source for 50 HP, 480 V motor drives (30% of volume)',
    requestedBy: 'sarah-chen',
    stage: 'Approvals',
    progress: 86,
    riskLevel: 'low',
    riskScore: supplier.riskScore,
    proposedRiskTier: KESTREL_RISK_TIER,
    aiSummary: {
      title: 'Ready for Procurement approval: 7 of 8 steps complete, low risk',
      body: `Bank account ownership is verified by a bank confirmation letter and a callback. Seven screenings are clear apart from one negative-news hit, rated low relevance to supply. The UL certificate names the Nordborg factory, and the EU safety declarations and ISO 9001 certificate are in order. Quality, Finance and Legal have approved. After approval two items stay open: the on-site audit on ${fmtDate(daysFromNow(18))} and sample unit KD-VFD-26-019, along with 2 quality agreement deviations to counter.`,
      tone: 'success',
    },
    riskDimensions: [
      { label: 'Financial', score: 19 },
      { label: 'Legal and compliance', score: 22 },
      { label: 'Quality and regulatory', score: 31 },
      { label: 'Sustainability', score: 26 },
      { label: 'Supply continuity', score: 24 },
    ],
    openedDaysAgo: 23,
    waitingOn: 'Pillar',
    waitingDetail: 'Procurement approval',
    contact: {
      name: supplier.primaryContact.name,
      title: supplier.primaryContact.title,
    },
    materialId: 'MAT-VFD',
    sourcingEventId: 'RFQ-2026-022',
    steps: {
      profile: { status: 'done', content: PROFILE },
      'tax-banking': { status: 'done', content: TAX_BANKING },
      financial: { status: 'done', content: FINANCIAL },
      legal: { status: 'done', content: LEGAL },
      quality: { status: 'attention', content: QUALITY },
      'insurance-esg': { status: 'done', content: INSURANCE_ESG },
      agreements: { status: 'attention', content: AGREEMENTS },
      approvals: { status: 'pending', content: APPROVALS },
    },
    agentLog: [
      {
        minutesAgo: 1 * MINUTES_PER_DAY + 40,
        text: `Routed Procurement approval to ${getPerson(PROCUREMENT_APPROVER).name}`,
        detail: 'Quality, Finance and Legal approved.',
      },
      {
        minutesAgo: 2 * MINUTES_PER_DAY + 90,
        text: 'Drafted the quality agreement from the Pillar template',
        detail: 'Flagged 2 deviations against the Pillar standard.',
      },
      {
        minutesAgo: 3 * MINUTES_PER_DAY + 150,
        text: `Scheduled the on-site audit with ${priya}`,
        detail: `${fmtDate(daysFromNow(18))}, Nordborg.`,
      },
      {
        minutesAgo: 3 * MINUTES_PER_DAY + 380,
        text: 'Verified bank account ownership by callback',
        detail:
          'Called the number in the company registry. Call note recorded.',
      },
      {
        minutesAgo: 3 * MINUTES_PER_DAY + 600,
        text: 'Flagged 1 negative-news hit and summarized it',
        detail: '2023 patent dispute, settled 2024. Low relevance to supply.',
      },
      {
        minutesAgo: 4 * MINUTES_PER_DAY + 60,
        text: 'Ran 7 screenings',
        detail:
          'US, EU, UK and UN sanctions lists, government officials, negative news, lawsuits.',
      },
      {
        minutesAgo: 4 * MINUTES_PER_DAY + 200,
        text: 'Extracted 64 fields from 10 documents',
        detail: 'Average confidence 96%.',
      },
      {
        minutesAgo: 4 * MINUTES_PER_DAY + 400,
        text: 'Chased 3 missing documents by email over 4 days',
        detail:
          'Foreign tax form, bank confirmation letter and certificate of insurance. All received.',
      },
      {
        minutesAgo: 6 * MINUTES_PER_DAY,
        text: `Sent a reminder and copied ${supplier.primaryContact.name}`,
      },
      {
        minutesAgo: 8 * MINUTES_PER_DAY,
        text: 'Opened the case and sent the document request',
        detail:
          'Portal invite for 10 documents, 3 of them missing after the first pass.',
      },
    ],
  };
}
